/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import { Button, Modal, TableProps } from 'antd';
import TableComponent from '../table';
import { ISearchInboundReq, IInboundItem } from '@/interfaces/inbound';
import { useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { searchInboundItemsError } from '@/adapters/inbound';
import { formatDate, formatNumber } from '@/utils/dataUtils';
import { EInboundStatus, EInboundType } from '@/enums/EInbound';
import businessDaysPlugin from '@/utils/dayjs.business';
import dayjs from 'dayjs';

interface IProps {
  open: boolean;
  doOrderId?: number;
  productId?: number;
  handleCancel: () => void;
  isSafetyView?: boolean;
}

const _criteria: ISearchInboundReq = {
  orderBy: 'id',
  orderType: 'descend',
  pageSize: -1,
  pageStart: 1,
  search: '',
  status: EInboundStatus.OPEN,
  type: EInboundType.NORMAL,
};

const InboundItemExternalModal = ({
  open,
  handleCancel,
  doOrderId,
  productId,
  isSafetyView = false,
}: IProps) => {
  dayjs.extend(businessDaysPlugin);
  const [criteria] = useState<ISearchInboundReq>({
    ..._criteria,
    doOrderId: doOrderId,
    productId: productId,
  });

  const columns: TableProps<IInboundItem>['columns'] = [
    {
      title: isSafetyView ? 'DOC COMPLETED' : 'INV DATE',
      dataIndex: 'invDate',
      key: 'invDate',
      align: 'center',
      width: 120,
      render: _ => formatDate(_),
    },
    {
      title: 'EST CLEAR DATE',
      dataIndex: 'invDate',
      key: 'invDate',
      className: 'font-bold',
      align: 'center',
      width: 120,
      hidden: !isSafetyView,
      render: (_, rec) =>
        ['SG'].includes(rec.consCountry || '')
          ? formatDate(dayjs(_).businessDaysAdd(10))
          : ['US'].includes(rec.consCountry || '')
            ? formatDate(dayjs(rec.arrivalDate).businessDaysAdd(4))
            : '',
    },
    {
      title: 'INV NUMBER',
      dataIndex: 'invNO',
      key: 'invNO',
      align: 'center',
      className: 'font-bold text-blue-700',
      width: 120,
    },
    {
      title: 'INCOMING CODE',
      dataIndex: 'productCode',
      key: 'productCode',
      className: 'font-bold text-blue-700',
      align: 'center',
      width: 180,
      hidden: isSafetyView,
    },
    {
      title: 'ITEM NAME',
      dataIndex: 'pdtDescriptionE',
      key: 'pdtDescriptionE',
      className: 'font-bold',
      hidden: isSafetyView,
    },
    {
      title: 'จำนวน',
      dataIndex: 'totalUnit',
      key: 'totalUnit',
      className: 'font-bold',
      width: 130,
      align: 'right',
      render: (_, rec) =>
        formatNumber(
          ['KGM', 'KG'].includes(rec.baseUnitId as string)
            ? rec.totalQty
            : rec.totalUnit,
        ),
    },
    {
      title: 'หน่วย',
      dataIndex: 'uom',
      key: 'uom',
      className: 'font-bold',
      width: 100,
      align: 'center',
      render: (_, rec) => rec.baseUnitId,
    },
    {
      title: 'รับเข้า',
      dataIndex: 'totalReceivedQty',
      key: 'totalReceivedQty',
      className: 'font-bold',
      width: 130,
      align: 'right',
      render: (_, rec) => formatNumber(rec.totalReceivedQty),
      hidden: isSafetyView,
    },
    {
      title: 'น้ำหนัก',
      dataIndex: 'totalQty',
      key: 'totalQty',
      className: 'font-bold',
      width: 130,
      align: 'right',
      render: _ => formatNumber(_),
    },
  ];

  const { data: errorItems, isLoading } = useQuery<
    AxiosResponse<IInboundItem[]>,
    Error
  >({
    queryKey: ['search-inbound-error-item', criteria],
    queryFn: async () => await searchInboundItemsError(criteria),
    enabled: !!criteria?.doOrderId || !!criteria?.productId,
  });

  return (
    <Modal
      title="Inbound items"
      open={open}
      width={'1200px'}
      onCancel={handleCancel}
      cancelText="ยกเลิก"
      footer={[
        <Button key="back" onClick={handleCancel}>
          ปิด
        </Button>,
      ]}
    >
      <div className="flex flex-col gap-3 w-full">
        <TableComponent<IInboundItem>
          columns={columns}
          criteria={undefined}
          data={{
            data: errorItems?.data || [],
            totalRecords: errorItems?.data?.length || 0,
          }}
          loading={isLoading}
          onTableChange={undefined}
          scrollLength={1150}
          scroll
          showPagination={false}
        />
      </div>
    </Modal>
  );
};

export default InboundItemExternalModal;
